/**
 * UserAvatar Component
 *
 * Standardized avatar component using shadcn/ui Avatar primitives.
 * Provides consistent fallback (User icon) and size variants across the app.
 *
 * @example
 * <UserAvatar src={avatarUrl} alt="Username" size="md" />
 */

import * as React from 'react';
import { User } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { getAvatarSrc } from '@/lib/storage/avatarUrl';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const sizeClasses: Record<AvatarSize, string> = {
  xs: 'h-6 w-6',
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
  xl: 'h-16 w-16',
};

const iconSizeClasses: Record<AvatarSize, string> = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
  xl: 'h-8 w-8',
};

export interface UserAvatarProps {
  /** Direct image URL */
  src?: string | null;
  /** Avatar URL from database (used with avatarVersion and avatarPath) */
  avatarUrl?: string | null;
  /** Avatar path for Supabase storage */
  avatarPath?: string | null;
  /** Avatar version for cache busting */
  avatarVersion?: number;
  /** Alt text for the image */
  alt?: string;
  /** Size variant */
  size?: AvatarSize;
  /** Additional class names */
  className?: string;
  /** Ring/border styling */
  ring?: boolean;
}

export function UserAvatar({
  src,
  avatarUrl,
  avatarPath,
  avatarVersion = 0,
  alt = 'User avatar',
  size = 'md',
  className,
  ring = false,
}: UserAvatarProps) {
  // Determine the image source
  const imageSrc = src || getAvatarSrc(avatarUrl, avatarVersion, avatarPath);

  return (
    <Avatar
      className={cn(
        sizeClasses[size],
        ring && 'ring-1 ring-border',
        className
      )}
    >
      {imageSrc && (
        <AvatarImage
          src={imageSrc}
          alt={alt}
          className="object-cover"
        />
      )}
      <AvatarFallback className="bg-muted">
        <User className={cn('text-muted-foreground', iconSizeClasses[size])} />
      </AvatarFallback>
    </Avatar>
  );
}

export default UserAvatar;
